import React, { useMemo } from 'react';
import { seeded } from './modes';

const ropeY = (f, scallops, sag) => {
  const s = (f * scallops) % 1;
  return 4 * sag * s * (1 - s);
};

const Rope = ({ width, scallops, sag, color, thick = 3 }) => {
  const seg = width / scallops;
  const d = Array.from({ length: scallops }, (_, k) => `M ${k * seg} 0 Q ${k * seg + seg / 2} ${2 * sag} ${(k + 1) * seg} 0`).join(' ');
  return (
    <svg width={width} height={sag + 4} viewBox={`0 0 ${width} ${sag + 4}`} className="absolute left-0 top-0" aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth={thick} strokeLinecap="round" />
    </svg>
  );
};

const spacingFor = (intensity, isMobile) => {
  if (isMobile) return 74;
  return { subtle: 150, normal: 115, festive: 90 }[intensity] || 115;
};

export const StringGarland = ({ garland, width, isMobile, intensity }) => {
  const scallops = isMobile ? 2 : Math.max(3, Math.round(width / 420));
  const sag = isMobile ? 18 : 28;
  const n = Math.max(2, Math.floor(width / spacingFor(intensity, isMobile)));
  const size = isMobile ? 18 : 24;
  return (
    <div className="szn-garland" style={{ top: 0, height: sag + 60 }} data-testid="szn-garland-string">
      <Rope width={width} scallops={scallops} sag={sag} color={garland.rope} />
      {Array.from({ length: n }, (_, i) => {
        const f = (i + 0.5) / n;
        return (
          <div key={i} className="szn-hang" style={{ left: `${f * 100}%`, top: ropeY(f, scallops, sag), animationDelay: `${(i % 5) * -0.9}s` }}>
            <span className="szn-thread" style={{ height: 8 + (i % 3) * 5 }} />
            <span className="szn-emoji" style={{ fontSize: size }}>{garland.items[i % garland.items.length]}</span>
          </div>
        );
      })}
      {garland.lights?.length > 0 && Array.from({ length: n + 1 }, (_, i) => {
        const f = i / n;
        if (f <= 0.01 || f >= 0.99) return null;
        const c = garland.lights[i % garland.lights.length];
        return <span key={`l${i}`} className="szn-light" style={{ left: `calc(${f * 100}% - 4px)`, top: ropeY(f, scallops, sag) - 2, background: c, boxShadow: `0 0 8px 2px ${c}`, animationDelay: `${(i % 4) * -0.45}s` }} />;
      })}
    </div>
  );
};

export const Bunting = ({ garland, width, isMobile, intensity }) => {
  const scallops = isMobile ? 2 : Math.max(3, Math.round(width / 420));
  const sag = isMobile ? 16 : 24;
  const n = Math.max(3, Math.floor(width / (isMobile ? 40 : spacingFor(intensity, isMobile) * 0.5)));
  const w = isMobile ? 16 : 24;
  const h = isMobile ? 22 : 32;
  return (
    <div className="szn-garland" style={{ top: 0, height: sag + h + 8 }} data-testid="szn-garland-bunting">
      <Rope width={width} scallops={scallops} sag={sag} color="rgba(60,60,60,0.55)" thick={2} />
      {Array.from({ length: n }, (_, i) => {
        const f = (i + 0.5) / n;
        return (
          <span
            key={i}
            className={`szn-flag szn-${garland.shape || 'triangle'}`}
            style={{ left: `calc(${f * 100}% - ${w / 2}px)`, top: ropeY(f, scallops, sag), width: w, height: h, background: garland.colors[i % garland.colors.length], animationDelay: `${(i % 6) * -0.8}s`, boxShadow: '0 3px 6px rgba(0,0,0,0.15)' }}
          />
        );
      })}
    </div>
  );
};

export const Icicles = ({ width, isMobile }) => {
  const icicles = useMemo(() => {
    const rnd = seeded(Math.round(width) + 7);
    const step = isMobile ? 20 : 26;
    const n = Math.ceil(width / step) + 1;
    return Array.from({ length: n }, (_, i) => ({ x: i * step, w: step * 0.9, h: (isMobile ? 10 : 14) + rnd() * (isMobile ? 26 : 40) }));
  }, [width, isMobile]);
  const H = isMobile ? 40 : 58;
  return (
    <svg className="szn-garland" style={{ top: -1 }} width={width} height={H} viewBox={`0 0 ${width} ${H}`} aria-hidden="true" data-testid="szn-garland-icicles">
      <defs>
        <linearGradient id="szn-ice" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="1" stopColor="#bfe6ff" stopOpacity="0.85" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width={width} height="4" fill="#ffffff" opacity="0.9" />
      {icicles.map((ic, i) => (
        <polygon key={i} points={`${ic.x},0 ${ic.x + ic.w},0 ${ic.x + ic.w / 2},${ic.h}`} fill="url(#szn-ice)" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,60,120,0.25))' }} />
      ))}
    </svg>
  );
};

export const Tags = ({ garland, isMobile }) => {
  const offsets = isMobile ? [12, 104] : [28, 170, 290];
  const threads = [26, 40, 32];
  return (
    <div className="szn-garland" style={{ top: 0, height: 90 }} data-testid="szn-garland-tags">
      {garland.tags.slice(0, isMobile ? 2 : 3).map((t, i) => (
        <div key={t.text} className="szn-tag" style={{ position: 'absolute', right: offsets[i], top: 0, animationDelay: `${i * -1.3}s` }}>
          <span className="szn-thread" style={{ height: threads[i], background: 'rgba(201,162,39,0.9)' }} />
          <span className="szn-tag-body" style={{ background: t.bg, color: t.color, fontSize: isMobile ? 9 : 11 }}>{t.text}</span>
        </div>
      ))}
    </div>
  );
};

const Web = ({ size, flip }) => {
  const rays = [0, 15, 30, 45, 60, 75, 90];
  const rings = [0.25, 0.5, 0.75, 1];
  const pt = (deg, r) => [Math.cos((deg * Math.PI) / 180) * r * size, Math.sin((deg * Math.PI) / 180) * r * size];
  const arcs = rings.map((r) =>
    rays.slice(0, -1).map((a, i) => {
      const [x1, y1] = pt(a, r);
      const [x2, y2] = pt(rays[i + 1], r);
      const [cx, cy] = pt((a + rays[i + 1]) / 2, r * 0.9);
      return `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;
    }).join(' ')
  ).join(' ');
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" style={{ transform: flip ? 'scaleX(-1)' : undefined, opacity: 0.55 }}>
      <g stroke="#6b7280" strokeWidth="1" fill="none">
        {rays.map((a) => { const [x, y] = pt(a, 1); return <line key={a} x1="0" y1="0" x2={x} y2={y} />; })}
        <path d={arcs} />
      </g>
    </svg>
  );
};

export const Cobweb = ({ isMobile }) => {
  const size = isMobile ? 90 : 150;
  return (
    <div className="szn-garland" style={{ top: 0, height: size + 20 }} data-testid="szn-garland-cobweb">
      <div className="absolute left-0 top-0"><Web size={size} /></div>
      <div className="absolute right-0 top-0"><Web size={size} flip /></div>
      <div className="szn-spider" style={{ left: '30%', top: 0 }}>
        <span className="szn-thread" style={{ background: 'rgba(60,60,60,0.6)' }} />
        <span className="szn-emoji" style={{ fontSize: isMobile ? 18 : 24 }}>🕷️</span>
      </div>
    </div>
  );
};

export const Garland = ({ garland, ...rest }) => {
  if (!garland) return null;
  switch (garland.type) {
    case 'string': return <StringGarland garland={garland} {...rest} />;
    case 'bunting': return <Bunting garland={garland} {...rest} />;
    case 'icicles': return <Icicles {...rest} />;
    case 'tags': return <Tags garland={garland} {...rest} />;
    case 'cobweb': return <Cobweb {...rest} />;
    default: return null;
  }
};
